import { useState, useEffect } from 'react';
import { Server } from 'lucide-react';
import { URL_PATTERNS, BACKEND_PROJECTS, API_RESOURCE_GROUP_MAP, APP_SERVICE_NAMES } from '../../config/urlConfig';
import { AZURE_ENV_TEMPLATE } from '../../config/azureEnvTemplate';
import { AZURE_API_TEMPLATES, AUCTION_ENGINE_ENV_TEMPLATE, COMPANY_API_ENV_TEMPLATE, CONTACTS_API_ENV_TEMPLATE, INVENTORY_API_ENV_TEMPLATE, SIMULCAST_API_ENV_TEMPLATE, SIMULCAST_AUCTION_ENGINE_ENV_TEMPLATE } from '../../config/azureApiTemplates';
import { pushAllAppSettings } from '../../services/appSettingsApi';

const CUSTOM_CONFIG_STORAGE_KEY = 'corsGeneratorCustomConfig';

function loadCustomConfig() {
  try {
    const saved = localStorage.getItem(CUSTOM_CONFIG_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        customVariables: (parsed.customVariables || []).map((v) => ({
          ...v,
          id: v.id || crypto.randomUUID(),
        })),
        customPlaceholders: (parsed.customPlaceholders || []).map((p) => ({
          ...p,
          id: p.id || crypto.randomUUID(),
        })),
      };
    }
  } catch (e) {
    console.warn('Failed to load CORS generator custom config:', e);
  }
  return { customVariables: [], customPlaceholders: [] };
}

function CORSGenerator() {
  // Form state - URLs used for CORS generation
  const [tenantName, setTenantName] = useState('');
  const [azureNewstorefrontUrl, setAzureNewstorefrontUrl] = useState('');
  const [customStorefrontDomain, setCustomStorefrontDomain] = useState('');
  const [customSimulcastDomain, setCustomSimulcastDomain] = useState('');
  const [azureKioskUrl, setAzureKioskUrl] = useState('');

  // Common Variables state (AppInsights, Storage, TenantKey, UseLegacyStoreFront)
  const [appInsightsInstrumentationKey, setAppInsightsInstrumentationKey] = useState('');
  const [appInsightsConnectionString, setAppInsightsConnectionString] = useState('');
  const [azureStorageConnectionString, setAzureStorageConnectionString] = useState('');
  const [azureStorageKey, setAzureStorageKey] = useState('');
  const [tenantKey, setTenantKey] = useState('');
  const [useLegacyStoreFront, setUseLegacyStoreFront] = useState('false');

  // API selection and API-specific fields
  const [selectedApi, setSelectedApi] = useState('');
  const [apiSpecificValues, setApiSpecificValues] = useState({});

  // Custom variables and placeholders
  const [customVariables, setCustomVariables] = useState([]);
  const [customPlaceholders, setCustomPlaceholders] = useState([]);

  // Output state
  const [generatedJson, setGeneratedJson] = useState('');
  const [generatedForApi, setGeneratedForApi] = useState('');
  const [generatedForApiName, setGeneratedForApiName] = useState('');
  const [jsonCopied, setJsonCopied] = useState(false);

  // Push to Azure state
  const [pushLoading, setPushLoading] = useState(false);
  const [pushResults, setPushResults] = useState(null);
  const [showPushConfirm, setShowPushConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    const { customVariables: cv, customPlaceholders: cp } = loadCustomConfig();
    setCustomVariables(cv);
    setCustomPlaceholders(cp);
  }, []);

  useEffect(() => {
    if (customVariables.length > 0 || customPlaceholders.length > 0) {
      localStorage.setItem(CUSTOM_CONFIG_STORAGE_KEY, JSON.stringify({
        customVariables,
        customPlaceholders,
      }));
    }
  }, [customVariables, customPlaceholders]);

  const buildCorsForApi = (apiId) => {
    const urls = [];
    Object.entries(URL_PATTERNS).forEach(([key, patternFn]) => {
      if (key === apiId) return;
      const url = patternFn(tenantName);
      if (url) {
        const protocol = url.startsWith('localhost') ? 'http://' : 'https://';
        urls.push(`${protocol}${url}`);
      }
    });
    const addUrl = (val) => {
      const url = (val || '').trim();
      if (!url) return;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        urls.push(`https://${url}`);
      } else {
        urls.push(url);
      }
    };
    addUrl(azureNewstorefrontUrl);
    addUrl(customStorefrontDomain);
    addUrl(customSimulcastDomain);
    addUrl(azureKioskUrl);
    return urls.join('|');
  };

  const formatUrl = (url) => {
    if (!url) return '';
    const trimmed = url.trim();
    if (!trimmed) return '';
    // If it already has http:// or https://, return as is
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }
    // Otherwise, add https://
    return `https://${trimmed}`;
  };

  // Reusable: generates the settings array for any API
  const generateSettingsForApi = (apiId) => {
    const apiConfig = AZURE_API_TEMPLATES[apiId];
    if (!apiConfig?.hasTemplate) return null;

    const corsValue = buildCorsForApi(apiId);
    const storefrontUrl = formatUrl(customStorefrontDomain);
    const simulcastUrl = formatUrl(customSimulcastDomain);

    const replacements = {
      tenantName,
      CORS: corsValue,
      APPINSIGHTS_INSTRUMENTATIONKEY: appInsightsInstrumentationKey,
      APPLICATIONINSIGHTS_CONNECTION_STRING: appInsightsConnectionString,
      'AzureStorage:ConnectionString': azureStorageConnectionString,
      'AzureStorage:Key': azureStorageKey,
      EquifyAuctionsURL: storefrontUrl,
      StoreFrontURL: storefrontUrl,
      SimulcastURL: simulcastUrl,
      TenantKey: tenantKey,
      UseLegacyStoreFront: useLegacyStoreFront,
      SignalRConnectionString: apiSpecificValues.signalRConnectionString || '',
      TenantConnectionsValue: apiSpecificValues.tenantConnectionsValue || '',
      ...Object.fromEntries(
        customPlaceholders
          .filter((p) => p.key && p.value !== undefined)
          .map((p) => {
            const k = p.key.trim().replace(/^\{|\}$/g, '');
            return [k, p.value];
          })
      ),
    };

    const applyReplacements = (str) => {
      if (!str || typeof str !== 'string') return str;
      let result = str;
      for (const [key, val] of Object.entries(replacements)) {
        if (val !== undefined && val !== null) {
          result = result.replace(new RegExp(`\\{${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\}`, 'g'), String(val));
        }
      }
      return result;
    };

    let baseTemplate;
    if (apiConfig.templateKey === 'auctionengine') {
      baseTemplate = JSON.parse(JSON.stringify(AUCTION_ENGINE_ENV_TEMPLATE));
    } else if (apiConfig.templateKey === 'company') {
      baseTemplate = JSON.parse(JSON.stringify(COMPANY_API_ENV_TEMPLATE));
    } else if (apiConfig.templateKey === 'contacts') {
      baseTemplate = JSON.parse(JSON.stringify(CONTACTS_API_ENV_TEMPLATE));
    } else if (apiConfig.templateKey === 'inventory') {
      baseTemplate = JSON.parse(JSON.stringify(INVENTORY_API_ENV_TEMPLATE));
    } else if (apiConfig.templateKey === 'simulcast') {
      baseTemplate = JSON.parse(JSON.stringify(SIMULCAST_API_ENV_TEMPLATE));
    } else if (apiConfig.templateKey === 'simulcast-auctionengine') {
      baseTemplate = JSON.parse(JSON.stringify(SIMULCAST_AUCTION_ENGINE_ENV_TEMPLATE));
    } else {
      baseTemplate = JSON.parse(JSON.stringify(AZURE_ENV_TEMPLATE));
    }

    const customVarsList = customVariables
      .filter((v) => v.name && v.name.trim())
      .map((v) => ({
        name: v.name.trim(),
        value: v.value || '',
        slotSetting: v.slotSetting !== false,
      }));
    const fullTemplate = [...baseTemplate, ...customVarsList];

    fullTemplate.forEach((item) => {
      item.name = applyReplacements(item.name);
      item.value = applyReplacements(item.value);
    });

    return fullTemplate;
  };

  const generateJSON = () => {
    if (!tenantName) {
      alert('Please provide tenant name');
      return;
    }

    const apiConfig = selectedApi ? AZURE_API_TEMPLATES[selectedApi] : null;
    if (!selectedApi || !apiConfig?.hasTemplate) {
      alert('Please select an API with an available template');
      return;
    }

    if (!appInsightsInstrumentationKey || !appInsightsConnectionString ||
        !azureStorageConnectionString || !azureStorageKey || !tenantKey || !useLegacyStoreFront) {
      alert('Please fill in all common variables');
      return;
    }

    if (apiConfig.templateKey === 'backoffice') {
      if (!customSimulcastDomain) {
        alert('Please fill in Custom Simulcast Domain');
        return;
      }
    } else if (apiConfig.templateKey === 'auctionengine') {
      if (!apiSpecificValues.signalRConnectionString?.trim()) {
        alert('Please fill in SignalR Connection String');
        return;
      }
    } else if (apiConfig.templateKey === 'inventory') {
      if (!apiSpecificValues.tenantConnectionsValue?.trim()) {
        alert('Please fill in TenantConnections value');
        return;
      }
    } else if (apiConfig.templateKey === 'simulcast-auctionengine') {
      if (!apiSpecificValues.signalRConnectionString?.trim()) {
        alert('Please fill in SignalR Connection String');
        return;
      }
    }

    const settings = generateSettingsForApi(selectedApi);
    if (!settings) return;

    const jsonString = JSON.stringify(settings, null, 2);
    setGeneratedJson(jsonString);
    setJsonCopied(false);
    setGeneratedForApi(selectedApi);
    const apiProject = BACKEND_PROJECTS.find((p) => p.id === selectedApi);
    setGeneratedForApiName(apiProject?.name || selectedApi);
  };

  // Validate fields needed to push all APIs at once
  const validateForPushAll = () => {
    if (!tenantName) {
      alert('Please provide tenant name');
      return false;
    }
    if (!appInsightsInstrumentationKey || !appInsightsConnectionString ||
        !azureStorageConnectionString || !azureStorageKey || !tenantKey || !useLegacyStoreFront) {
      alert('Please fill in all common variables');
      return false;
    }
    if (!apiSpecificValues.signalRConnectionString?.trim()) {
      alert('Please fill in SignalR Connection String (required for Auction Engine and Simulcast Auction Engine)');
      return false;
    }
    if (!apiSpecificValues.tenantConnectionsValue?.trim()) {
      alert('Please fill in TenantConnections value (required for Inventory API)');
      return false;
    }
    return true;
  };

  const handlePushAllToAzure = () => {
    if (!validateForPushAll()) return;
    setShowPushConfirm(true);
  };

  const executePushAll = async () => {
    setShowPushConfirm(false);
    setPushLoading(true);
    setPushResults(null);

    try {
      const tenant = tenantName.trim().toLowerCase();
      const batches = [];

      for (const project of BACKEND_PROJECTS) {
        const apiConfig = AZURE_API_TEMPLATES[project.id];
        if (!apiConfig?.hasTemplate) continue;

        const settings = generateSettingsForApi(project.id);
        if (!settings) continue;

        const appServiceNameFn = APP_SERVICE_NAMES[project.id];
        const rgSuffix = API_RESOURCE_GROUP_MAP[project.id];
        if (!appServiceNameFn || !rgSuffix) continue;

        batches.push({
          appServiceName: appServiceNameFn(tenant),
          resourceGroupName: `${tenant}-${rgSuffix}`,
          settings: settings.map((s) => ({
            name: s.name,
            value: s.value,
            slotSetting: s.slotSetting,
          })),
        });
      }

      const results = await pushAllAppSettings(batches);
      setPushResults(results);
    } catch (err) {
      setPushResults([{
        appServiceName: '(all)',
        success: false,
        message: err.message || 'Failed to push app settings.',
      }]);
    } finally {
      setPushLoading(false);
    }
  };

  const addCustomVariable = () => {
    setCustomVariables((prev) => [
      ...prev,
      { id: crypto.randomUUID(), name: '', value: '', slotSetting: true },
    ]);
  };

  const updateCustomVariable = (id, field, value) => {
    setCustomVariables((prev) =>
      prev.map((v) => (v.id === id ? { ...v, [field]: value } : v))
    );
  };

  const removeCustomVariable = (id) => {
    setCustomVariables((prev) => prev.filter((v) => v.id !== id));
  };

  const addCustomPlaceholder = () => {
    setCustomPlaceholders((prev) => [
      ...prev,
      { id: crypto.randomUUID(), key: '', value: '' },
    ]);
  };

  const updateCustomPlaceholder = (id, field, value) => {
    setCustomPlaceholders((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: value } : p))
    );
  };

  const removeCustomPlaceholder = (id) => {
    setCustomPlaceholders((prev) => prev.filter((p) => p.id !== id));
  };

  const setApiSpecificValue = (fieldId, value) => {
    setApiSpecificValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const copyJsonToClipboard = async () => {
    if (generatedJson) {
      try {
        await navigator.clipboard.writeText(generatedJson);
        setJsonCopied(true);
        setTimeout(() => setJsonCopied(false), 2000);
      } catch (err) {
        alert('Failed to copy to clipboard');
      }
    }
  };

  const downloadJson = () => {
    if (generatedJson && tenantName && generatedForApi) {
      const apiConfig = AZURE_API_TEMPLATES[generatedForApi];
      const suffix = apiConfig?.downloadSuffix || 'env';
      const blob = new Blob([generatedJson], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tenantName}-${suffix}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const tabs = [
    { id: 'general', label: 'General Config' },
    { id: 'endpoints', label: 'Endpoints & URLs' },
    { id: 'secrets', label: 'Secrets & Keys' },
  ];

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Azure Variables</h2>
        <button
          onClick={generateJSON}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
        >
          Generate JSON
        </button>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden">
        <div className="flex border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-950">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === tab.id
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-white dark:bg-slate-900'
                  : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-8 space-y-6">
          {activeTab === 'general' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Tenant Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={tenantName}
                  onChange={(e) => setTenantName(e.target.value)}
                  placeholder="e.g., jandj"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none dark:bg-slate-800 dark:text-gray-100"
                />
                <p className="text-xs text-gray-400 dark:text-slate-500">Used for resource group naming and identification.</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Select API for Environment Variables *</label>
                <select
                  value={selectedApi}
                  onChange={(e) => setSelectedApi(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-slate-800 dark:text-gray-100"
                >
                  <option value="">-- Select an API --</option>
                  {BACKEND_PROJECTS.map((project) => {
                    const config = AZURE_API_TEMPLATES[project.id];
                    const hasTemplate = config?.hasTemplate;
                    const label = hasTemplate ? project.name : `${project.name} (Template not yet configured)`;
                    return (
                      <option key={project.id} value={project.id} disabled={!hasTemplate}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">UseLegacyStoreFront *</label>
                <select
                  value={useLegacyStoreFront}
                  onChange={(e) => setUseLegacyStoreFront(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-slate-800 dark:text-gray-100"
                >
                  <option value="false">false</option>
                  <option value="true">true</option>
                </select>
              </div>
            </div>
          )}

          {activeTab === 'endpoints' && (
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900 dark:text-gray-100 flex items-center">
                <Server className="w-4 h-4 mr-2 text-gray-400 dark:text-slate-500" />
                Frontend Configuration
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Newstorefront Frontend URL</label>
                  <div className="flex rounded-lg shadow-sm">
                    <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-950 text-gray-500 dark:text-slate-400 text-sm">https://</span>
                    <input
                      type="text"
                      value={azureNewstorefrontUrl}
                      onChange={(e) => setAzureNewstorefrontUrl(e.target.value)}
                      placeholder="green-forest-0269.azurestaticapps.net"
                      className="flex-1 min-w-0 block w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-r-lg focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-slate-800 dark:text-gray-100"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Custom Storefront Domain</label>
                  <input
                    type="text"
                    value={customStorefrontDomain}
                    onChange={(e) => setCustomStorefrontDomain(e.target.value)}
                    placeholder="storefront.jandjauction.com"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none dark:bg-slate-800 dark:text-gray-100"
                  />
                  <p className="text-xs text-gray-400 dark:text-slate-500 italic">Added to CORS automatically.</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Custom Simulcast Domain</label>
                  <input
                    type="text"
                    value={customSimulcastDomain}
                    onChange={(e) => setCustomSimulcastDomain(e.target.value)}
                    placeholder="auction.jandjauction.com"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none dark:bg-slate-800 dark:text-gray-100"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Azure Kiosk URL</label>
                  <input
                    type="text"
                    value={azureKioskUrl}
                    onChange={(e) => setAzureKioskUrl(e.target.value)}
                    placeholder="kiosk.example.com"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none dark:bg-slate-800 dark:text-gray-100"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'secrets' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">APPINSIGHTS_INSTRUMENTATIONKEY *</label>
                  <input
                    type="text"
                    value={appInsightsInstrumentationKey}
                    onChange={(e) => setAppInsightsInstrumentationKey(e.target.value)}
                    placeholder="e.g., 6ffc7d34-7b0a-46d0-b9ec-2f84adace448"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none dark:bg-slate-800 dark:text-gray-100"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">APPLICATIONINSIGHTS_CONNECTION_STRING *</label>
                  <input
                    type="text"
                    value={appInsightsConnectionString}
                    onChange={(e) => setAppInsightsConnectionString(e.target.value)}
                    placeholder="InstrumentationKey=...;IngestionEndpoint=..."
                    className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none dark:bg-slate-800 dark:text-gray-100"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">AzureStorage:ConnectionString *</label>
                  <input
                    type="text"
                    value={azureStorageConnectionString}
                    onChange={(e) => setAzureStorageConnectionString(e.target.value)}
                    placeholder="DefaultEndpointsProtocol=https;AccountName=..."
                    className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none dark:bg-slate-800 dark:text-gray-100"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">AzureStorage:Key *</label>
                  <input
                    type="text"
                    value={azureStorageKey}
                    onChange={(e) => setAzureStorageKey(e.target.value)}
                    placeholder="Storage account key"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none dark:bg-slate-800 dark:text-gray-100"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">TenantKey *</label>
                  <input
                    type="text"
                    value={tenantKey}
                    onChange={(e) => setTenantKey(e.target.value)}
                    placeholder="e.g., 10020"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none dark:bg-slate-800 dark:text-gray-100"
                  />
                </div>
              </div>

              {selectedApi && AZURE_API_TEMPLATES[selectedApi]?.hasTemplate && AZURE_API_TEMPLATES[selectedApi].extraFields?.length > 0 && (
                <div className="border-t border-gray-100 dark:border-slate-800 pt-6 space-y-4">
                  <h3 className="font-medium text-gray-900 dark:text-gray-100">API-specific Fields</h3>
                  {AZURE_API_TEMPLATES[selectedApi].extraFields.map((field) => (
                    <div key={field.id} className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{field.label}</label>
                      <input
                        type={field.type || 'text'}
                        value={apiSpecificValues[field.id] || ''}
                        onChange={(e) => setApiSpecificValue(field.id, e.target.value)}
                        placeholder={field.placeholder}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none dark:bg-slate-800 dark:text-gray-100"
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="border-t border-gray-100 dark:border-slate-800 pt-6 space-y-4">
                <h3 className="font-medium text-gray-900 dark:text-gray-100">Custom Variables</h3>
                <button type="button" onClick={addCustomVariable} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
                  + Add Variable
                </button>
                {customVariables.map((v) => (
                  <div key={v.id} className="flex flex-wrap gap-2 items-center">
                    <input
                      type="text"
                      placeholder="Name"
                      value={v.name}
                      onChange={(e) => updateCustomVariable(v.id, 'name', e.target.value)}
                      className="flex-1 min-w-[120px] px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-gray-100"
                    />
                    <input
                      type="text"
                      placeholder="Value"
                      value={v.value}
                      onChange={(e) => updateCustomVariable(v.id, 'value', e.target.value)}
                      className="flex-1 min-w-[120px] px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-gray-100"
                    />
                    <label className="flex items-center gap-1 text-sm">
                      <input type="checkbox" checked={v.slotSetting !== false} onChange={(e) => updateCustomVariable(v.id, 'slotSetting', e.target.checked)} />
                      Slot
                    </label>
                    <button type="button" onClick={() => removeCustomVariable(v.id)} className="text-red-600 hover:text-red-700 text-sm">Remove</button>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-100 dark:border-slate-800 pt-6 space-y-4">
                <h3 className="font-medium text-gray-900 dark:text-gray-100">Custom Placeholders</h3>
                <button type="button" onClick={addCustomPlaceholder} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
                  + Add Placeholder
                </button>
                {customPlaceholders.map((p) => (
                  <div key={p.id} className="flex flex-wrap gap-2 items-center">
                    <input
                      type="text"
                      placeholder="{Key}"
                      value={p.key}
                      onChange={(e) => updateCustomPlaceholder(p.id, 'key', e.target.value)}
                      className="flex-1 min-w-[120px] px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-gray-100"
                    />
                    <input
                      type="text"
                      placeholder="Value"
                      value={p.value}
                      onChange={(e) => updateCustomPlaceholder(p.id, 'value', e.target.value)}
                      className="flex-1 min-w-[120px] px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-gray-100"
                    />
                    <button type="button" onClick={() => removeCustomPlaceholder(p.id)} className="text-red-600 hover:text-red-700 text-sm">Remove</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {generatedJson && (
        <div className="mt-8 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-slate-700 flex flex-wrap justify-between items-center gap-2">
            <h2 className="font-semibold text-gray-900 dark:text-gray-100">Generated Environment Variables — {generatedForApiName}</h2>
            <div className="flex gap-2">
              <button onClick={copyJsonToClipboard} className="px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 text-sm font-medium">
                {jsonCopied ? '✓ Copied!' : 'Copy to Clipboard'}
              </button>
              <button onClick={downloadJson} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                Download JSON
              </button>
            </div>
          </div>
          <pre className="p-6 bg-gray-50 dark:bg-slate-950 overflow-auto text-sm font-mono text-gray-800 dark:text-gray-200 max-h-96"><code>{generatedJson}</code></pre>
        </div>
      )}

      <div className="mt-8 pt-6 border-t border-gray-200 dark:border-slate-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Push All to Azure</h2>
        <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">Push environment variables to all 7 backend APIs. Fill in SignalR and TenantConnections below.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">SignalR Connection String *</label>
            <input
              type="text"
              value={apiSpecificValues.signalRConnectionString || ''}
              onChange={(e) => setApiSpecificValue('signalRConnectionString', e.target.value)}
              placeholder="Endpoint=...;AccessKey=..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none dark:bg-slate-800 dark:text-gray-100"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">TenantConnections value *</label>
            <input
              type="text"
              value={apiSpecificValues.tenantConnectionsValue || ''}
              onChange={(e) => setApiSpecificValue('tenantConnectionsValue', e.target.value)}
              placeholder="Connection string for TenantConnections"
              className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none dark:bg-slate-800 dark:text-gray-100"
            />
          </div>
        </div>
        <button
          onClick={handlePushAllToAzure}
          disabled={pushLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 font-medium"
        >
          {pushLoading ? 'Pushing to Azure...' : 'Push All to Azure'}
        </button>

        {showPushConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4" onClick={() => setShowPushConfirm(false)}>
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold mb-4 dark:text-gray-100">Confirm Push to Azure</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">This will replace all application settings on 7 App Services for tenant <strong>{tenantName}</strong>:</p>
              <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 mb-4">
                {BACKEND_PROJECTS.filter((p) => AZURE_API_TEMPLATES[p.id]?.hasTemplate).map((project) => (
                  <li key={project.id}>{APP_SERVICE_NAMES[project.id]?.(tenantName.trim().toLowerCase())}</li>
                ))}
              </ul>
              <p className="text-sm text-red-600 mb-4">This action cannot be undone.</p>
              <div className="flex gap-2">
                <button onClick={() => setShowPushConfirm(false)} className="flex-1 px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600">Cancel</button>
                <button onClick={executePushAll} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Push to All APIs</button>
              </div>
            </div>
          </div>
        )}

        {pushResults && (
          <div className="mt-4 p-4 bg-gray-50 dark:bg-slate-950 rounded-lg">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Push Results</h3>
            <div className="space-y-2">
              {pushResults.map((result, idx) => (
                <div key={idx} className={`flex items-center gap-2 p-2 rounded ${result.success ? 'bg-green-50 dark:bg-green-950 text-green-800' : 'bg-red-50 dark:bg-red-950 text-red-800'}`}>
                  <span>{result.success ? '✓' : '✗'}</span>
                  <span className="font-medium">{result.appServiceName}</span>
                  <span>{result.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CORSGenerator;
